import { ArrowDownTrayIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { TotalsRow } from 'components/TableTotals'
import { itemCountPerPage } from 'config'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  createAccountingAccountTransaction,
  deleteAccountingAccountTransaction,
  downloadGeneralLedger,
  getAccountingAccount,
  getGeneralLedger,
  getGeneralLedgerEntry,
  getGeneralLedgerNextRefNo,
  updateAccountingAccountTransaction,
} from 'services'
import { svgLoading } from 'stories/assets'
import { Button, Pagination } from 'stories/components'
import { confirm,formatDate, thousandSeperator } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { AccountTransactions } from '../AccountTransactions'
import { EditAccountTransactionDialog } from '../AccountTransactions/EditAccountTransactionDialog'
import type { IAccountTransaction } from '../AccountTransactions/types'

interface IGeneralLedgerEntry {
  id: number
  date: string
  accountNo: number
  accountName: string
  transactionType: string
  refNo: string
  loanNumber?: number | null
  payee: string
  memo: string
  debit: number
  credit: number
  balance: number
  journalId?: string
}

export const GeneralLedger = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState<IGeneralLedgerEntry[]>([])
  const [accounts, setAccounts] = useState<any[]>([])
  const [totalCount, setTotalCount] = useState(0)
  // Debit/credit over the whole filtered ledger, summed server-side: the grid holds one page.
  const [totals, setTotals] = useState<{ debit: number; credit: number } | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [editItem, setEditItem] = useState<IAccountTransaction | null>(null)
  const [suggestedRefNo, setSuggestedRefNo] = useState('')
  const [registerAccountNo, setRegisterAccountNo] = useState<number | null>(null)
  const [filters, setFilters] = useState({
    accountNo: '',
    startDate: '',
    endDate: '',
    search: '',
    loanNumbers: '',
    orderBy: 'date',
    orderDir: '-1',
    pageNum: 0,
    count: itemCountPerPage,
  })

  useEffect(() => {
    getAccountingAccount().then((res) => {
      setAccounts(res)
    })
  }, [])

  useEffect(() => {
    if (accounts.length > 0 && registerAccountNo == null) {
      fetch()
    }
  }, [filters, accounts, registerAccountNo])

  const fetch = async () => {
    setLoading(true)
    try {
      const result = await getGeneralLedger(filters)
      setData(result.results || [])
      setTotalCount(result.totalCount || 0)
      setTotals(result.totals || null)
    } catch (error) {
      console.error('Error fetching general ledger:', error)
      setData([])
      setTotalCount(0)
    }
    setLoading(false)
  }

  const onChangeFilter = (key: string, value: any) => {
    const newFilters: any = cloneDeep(filters)
    newFilters[key] = value
    newFilters.pageNum = 0
    setFilters(newFilters)
  }

  const onPageNavigate = (num: number) => {
    const newFilters = cloneDeep(filters)
    newFilters.pageNum = num
    setFilters(newFilters)
  }

  const onDownload = async () => {
    setLoading(true)
    try {
      await downloadGeneralLedger({
        accountNo: filters.accountNo || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        search: filters.search || undefined,
        loanNumbers: filters.loanNumbers || undefined,
        orderBy: filters.orderBy,
        orderDir: '1',
      })
    } catch (e: any) {
      toast(e?.response?.data?.message || e?.message || 'Download failed', { type: 'error' })
    }
    setLoading(false)
  }

  const openAdd = async () => {
    setLoading(true)
    try {
      const res = await getGeneralLedgerNextRefNo()
      setSuggestedRefNo(res?.refNo || '')
    } catch {
      setSuggestedRefNo('')
    }
    setLoading(false)
    setEditItem(null)
    setShowAdd(true)
  }

  const onOpenAccountRegister = (e: React.MouseEvent, accountNo: number) => {
    e.stopPropagation()
    setRegisterAccountNo(accountNo)
  }

  const onRowClick = async (item: IGeneralLedgerEntry) => {
    setLoading(true)
    try {
      const entry = await getGeneralLedgerEntry(item.id)
      setEditItem(entry)
      setShowAdd(false)
    } catch (e: any) {
      toast(e?.response?.data?.message || e?.message || 'Failed to open journal entry', { type: 'error' })
    }
    setLoading(false)
  }

  const onDelete = async (e: React.MouseEvent, item: IGeneralLedgerEntry) => {
    e.stopPropagation()
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Are you sure you want to delete this general ledger entry?
        <br />
        <span className="text-gray-600">{item.refNo ? `Ref No: ${item.refNo}` : item.accountName}</span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    setLoading(true)
    try {
      await deleteAccountingAccountTransaction(item.id, item.accountNo)
      toast('General ledger entry deleted.', { type: 'success' })
      await fetch()
    } catch (err: any) {
      toast(err?.response?.data?.message || err?.message || 'Failed to delete entry', { type: 'error' })
    }
    setLoading(false)
  }

  const entryOutsideActiveFilter = (item: IAccountTransaction) => {
    const date = String(item.date || '').slice(0, 10)
    if (filters.startDate && date < filters.startDate) return true
    if (filters.endDate && date > filters.endDate) return true
    if (filters.search) {
      const q = filters.search.toLowerCase()
      const hay = `${item.refNo || ''} ${item.payee || ''} ${item.memo || ''} ${item.loanNumber ?? ''}`.toLowerCase()
      if (!hay.includes(q)) return true
    }
    if (filters.loanNumbers) {
      const nos = filters.loanNumbers
        .split(/[\s,]+/)
        .map((t) => t.trim())
        .filter(Boolean)
        .map(Number)
        .filter((n) => !Number.isNaN(n))
      if (nos.length && (item.loanNumber == null || !nos.includes(Number(item.loanNumber)))) return true
    }
    if (filters.accountNo && Number(item.accountNo) !== Number(filters.accountNo)) {
      const distAccounts = (item.distributionLines || []).map((l) => Number(l.account))
      if (!distAccounts.includes(Number(filters.accountNo))) return true
    }
    return false
  }

  const onModalClose = async (item: IAccountTransaction | null) => {
    const wasEdit = !!editItem
    setShowAdd(false)
    setEditItem(null)
    if (!item) return

    setLoading(true)
    try {
      if (wasEdit && item.id > 0) {
        const res = await updateAccountingAccountTransaction(item.id, Number(item.accountNo), item)
        if (!(res as any)?.success && (res as any)?.success !== undefined) {
          toast((res as any)?.message || 'Failed to update transaction', { type: 'error' })
          setLoading(false)
          return
        }
        toast(`Updated transaction${item.refNo ? ` (${item.refNo})` : ''}.`, { type: 'success' })
      } else {
        const res = await createAccountingAccountTransaction(item)
        if (!(res as any)?.success && (res as any)?.success !== undefined) {
          toast((res as any)?.message || 'Failed to create transaction', { type: 'error' })
          setLoading(false)
          return
        }
        const refLabel = item.refNo ? ` (${item.refNo})` : ''
        if (entryOutsideActiveFilter(item)) {
          toast(
            `Posted${refLabel}. It falls outside the current filters, so it may not appear in this list.`,
            { type: 'info' },
          )
        } else {
          toast(`Posted transaction${refLabel}.`, { type: 'success' })
        }
      }
      await fetch()
    } catch (e: any) {
      toast(e?.response?.data?.message || e?.message || 'Failed to save transaction', { type: 'error' })
    }
    setLoading(false)
  }

  const accountOptions: Record<string, string> = { '': 'All Accounts' }
  accounts.forEach((acc) => {
    accountOptions[acc.no.toString()] = `${acc.name} (${acc.number})`
  })

  const filterAccountNo = filters.accountNo ? Number(filters.accountNo) : undefined

  if (registerAccountNo != null) {
    return (
      <AccountTransactions accountNo={registerAccountNo} onBack={() => setRegisterAccountNo(null)} />
    )
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center pb-5">
          General Ledger
          {isLoading && (
            <span className="ml-3">
              <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
            </span>
          )}
        </h1>
        <div className="flex gap-2">
          <Button color="blue" onClick={onDownload} className="">
            <div className="flex items-center gap-2">
              <ArrowDownTrayIcon className="w-4 h-4" />
              Download
            </div>
          </Button>
          <Button onClick={openAdd} className="">
            Add Transaction
          </Button>
        </div>
      </div>

      <div className="relative overflow-auto">
        <LayoutLoading show={isLoading} />

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
          <div>
            <RenderInput
              input={{
                inputType: 'select',
                title: 'Account',
                options: accountOptions,
                hasDefaultOption: false,
                value: filters.accountNo,
              }}
              Key="accountNo"
              onChange={(key: string, value: any) => onChangeFilter(key, value)}
            />
          </div>
          <div>
            <RenderInput
              input={{
                inputType: 'text',
                type: 'date',
                title: 'Start Date',
                value: filters.startDate,
              }}
              Key="startDate"
              onChange={(key: string, value: any) => onChangeFilter(key, value)}
            />
          </div>
          <div>
            <RenderInput
              input={{
                inputType: 'text',
                type: 'date',
                title: 'End Date',
                value: filters.endDate,
              }}
              Key="endDate"
              onChange={(key: string, value: any) => onChangeFilter(key, value)}
            />
          </div>
          <div>
            <RenderInput
              input={{
                inputType: 'text',
                title: 'Loan Number(s)',
                placeholder: 'e.g. 1001, 1002, 1005',
                value: filters.loanNumbers,
              }}
              Key="loanNumbers"
              onChange={(key: string, value: any) => onChangeFilter(key, value)}
            />
          </div>
          <div>
            <RenderInput
              input={{
                inputType: 'text',
                title: 'Search',
                placeholder: 'Ref No, payee, memo, or loan #',
                value: filters.search,
              }}
              Key="search"
              onChange={(key: string, value: any) => onChangeFilter(key, value)}
            />
          </div>
        </div>

        <div className="sm:rounded-lg mb-8 overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-3 py-3">
                  Date
                </th>
                <th scope="col" className="px-3 py-3">
                  Account
                </th>
                <th scope="col" className="px-3 py-3">
                  Type
                </th>
                <th scope="col" className="px-3 py-3">
                  Ref No
                </th>
                <th scope="col" className="px-3 py-3">
                  Loan #
                </th>
                <th scope="col" className="px-3 py-3">
                  Payee
                </th>
                <th scope="col" className="px-3 py-3">
                  Memo
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  Debit
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  Credit
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  Balance
                </th>
                <th scope="col" className="px-3 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={11} className="px-3 py-8 text-center text-gray-500">
                    No transactions found. Please select filters and search.
                  </td>
                </tr>
              )}
              {data.map((item) => {
                return (
                  <tr
                    className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 cursor-pointer"
                    key={item.id}
                    onClick={() => onRowClick(item)}
                    title="Open journal entry"
                  >
                    <td className="px-3 py-2 whitespace-nowrap">{formatDate(item.date)}</td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        className="text-shade-blue hover:underline text-left"
                        onClick={(e) => onOpenAccountRegister(e, item.accountNo)}
                        title="Open account register"
                      >
                        {item.accountName}
                      </button>
                    </td>
                    <td className="px-3 py-2">{item.transactionType}</td>
                    <td className="px-3 py-2">{item.refNo}</td>
                    <td className="px-3 py-2">{item.loanNumber ?? ''}</td>
                    <td className="px-3 py-2">{item.payee}</td>
                    <td className="px-3 py-2">{item.memo}</td>
                    <td className="px-3 py-2 text-right">{item.debit ? `$${thousandSeperator(item.debit)}` : ''}</td>
                    <td className="px-3 py-2 text-right">{item.credit ? `$${thousandSeperator(item.credit)}` : ''}</td>
                    <td className="px-3 py-2 text-right font-semibold">${thousandSeperator(item.balance)}</td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        className="p-1 text-red-600 hover-shadow1 rounded"
                        onClick={(e) => onDelete(e, item)}
                        title="Delete entry"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
            {data.length > 0 && totals && (
              <TotalsRow
                tdClass="px-3 py-2"
                cells={['Totals', '', '', '', '', '', '', totals.debit, totals.credit, '', '']}
              />
            )}
          </table>
        </div>

        {totalCount > 0 && (
          <div className="flex justify-end items-center mt-3 mb-3">
            <Pagination
              totalCount={totalCount}
              itemCountPerPage={itemCountPerPage}
              onNavigate={onPageNavigate}
              pageNum={filters.pageNum}
            />
          </div>
        )}
      </div>

      {(showAdd || editItem) && (
        <EditAccountTransactionDialog
          item={editItem}
          accountNo={editItem ? Number(editItem.accountNo) : filterAccountNo}
          allAccounts={accounts}
          openedFromLedger
          suggestedRefNo={editItem ? undefined : suggestedRefNo}
          onClose={onModalClose}
        />
      )}
    </div>
  )
}
